/**
 * useSlashCommandHandler — Slash command execution logic.
 * Extracted from SideChat.tsx send() function.
 * Each command is a separate handler function.
 * Rule 94: Modüler — slash commands ayrı modül.
 */
import { useCallback } from 'react'
import { API_URL, apiFetch } from '../../../config/api'
import type { ChatMessage } from '../../../contexts/ChatContext'
import { getHelpText, parseSlashCommand } from './useSlashCommands'

interface SlashHandlerOptions {
  addMessage: (msg: ChatMessage) => void
  updateLastMessage: (updates: Partial<ChatMessage>) => void
  setSending: (v: boolean) => void
  setCategory: (c: string) => void
  speak: (text: string) => void
  activeAgentName: string | undefined
}

function ts(): string {
  return new Date().toLocaleTimeString()
}

export function useSlashCommandHandler(opts: SlashHandlerOptions) {
  const { addMessage, updateLastMessage, setSending, setCategory, speak, activeAgentName } = opts

  const handleSlashCommand = useCallback(async (text: string): Promise<boolean> => {
    const cmd = parseSlashCommand(text)
    if (!cmd) return false

    addMessage({ role: 'user', content: text, ts: ts() })

    // /help
    if (cmd.command === '/help') {
      addMessage({ role: 'assistant', content: getHelpText(), ts: ts(), agentName: 'System' })
      return true
    }

    // /todo [title]
    if (cmd.command === '/todo' && cmd.args) {
      try {
        const res = await apiFetch(`${API_URL}/api/tasks`, {
          method: 'POST',
          body: JSON.stringify({ title: cmd.args, category: 'personal', priority: 'medium', status: 'todo' }),
          headers: { 'Content-Type': 'application/json' },
        })
        const d = await res.json() as { ok?: boolean; id?: number; error?: string }
        if (d.ok || d.id) {
          addMessage({ role: 'assistant', content: `✅ Task added: "${cmd.args}"`, ts: ts(), agentName: 'System' })
          setCategory('todo')
        } else {
          addMessage({ role: 'assistant', content: `❌ Could not add: ${d.error ?? 'Unknown error'}`, ts: ts(), agentName: 'System' })
        }
      } catch {
        addMessage({ role: 'assistant', content: '❌ Connection error', ts: ts(), agentName: 'System' })
      }
      return true
    }

    // /research [konu]
    if (cmd.command === '/research' && cmd.args) {
      addMessage({ role: 'assistant', content: `🔬 Araştırma başlatılıyor: "${cmd.args}"...`, ts: ts(), agentName: 'System', status: 'thinking' })
      setSending(true)
      try {
        const res = await apiFetch(`${API_URL}/api/research/projects`, {
          method: 'POST',
          body: JSON.stringify({ title: cmd.args, description: `Research on ${cmd.args}` }),
          headers: { 'Content-Type': 'application/json' },
        })
        const d = await res.json() as { ok?: boolean; project_id?: number; id?: number; error?: string }
        if (d.ok || d.project_id) {
          updateLastMessage({
            content: `🔬 Araştırma projesi oluşturuldu: "${cmd.args}"\n\nProje ID: ${d.project_id ?? d.id}\n\nAraştırma sonuçları brain_wiki'ye kaydedilecek.`,
            status: 'done',
          })
        } else {
          updateLastMessage({ content: `❌ Could not start research: ${d.error ?? 'Unknown error'}`, status: 'done' })
        }
      } catch (e: any) {
        updateLastMessage({ content: `❌ Connection error: ${e.message}`, status: 'done' })
      } finally {
        setSending(false)
      }
      return true
    }

    // /askai [soru]
    if (cmd.command === '/askai' && cmd.args) {
      addMessage({ role: 'assistant', content: '...', ts: ts(), agentName: 'AskAI', status: 'thinking' })
      setSending(true)
      try {
        const res = await apiFetch(`${API_URL}/api/user-chat/send`, {
          method: 'POST',
          body: JSON.stringify({ agent_id: 522, message: cmd.args }),
          headers: { 'Content-Type': 'application/json' },
        })
        const d = await res.json() as { response?: string; error?: string }
        updateLastMessage({ content: d.response ?? d.error ?? 'No response', status: 'done' })
        speak(d.response ?? '')
      } catch {
        updateLastMessage({ content: '⚠ Connection error', status: 'done' })
      } finally {
        setSending(false)
      }
      return true
    }

    // /skill run [name]
    if (cmd.command === '/skill' && cmd.args.startsWith('run ')) {
      const skillName = cmd.args.replace('run ', '').trim()
      addMessage({ role: 'assistant', content: `⚡ Running skill: ${skillName}...`, ts: ts(), agentName: 'System', status: 'thinking' })
      setSending(true)
      try {
        const searchRes = await apiFetch(`${API_URL}/api/skills?search=${encodeURIComponent(skillName)}`)
        const skills = ((await searchRes.json()) as { skills: { id: number; name: string }[] }).skills ?? []
        const skill = skills.find(s => s.name.toLowerCase().includes(skillName.toLowerCase()))
        if (!skill) {
          updateLastMessage({ content: `❌ Skill not found: "${skillName}"`, status: 'done' })
        } else {
          const runRes = await apiFetch(`${API_URL}/api/aicloud/skills/${skill.id}/run`, {
            method: 'POST',
            body: JSON.stringify({ parameters: {}, agent_id: 1 }),
            headers: { 'Content-Type': 'application/json' },
          })
          const d = await runRes.json() as { result?: string; status?: string; duration_seconds?: number }
          const resultText = d.status === 'failed'
            ? `❌ ${skill.name} başarısız: ${d.result}`
            : `✅ ${skill.name} tamamlandı${d.duration_seconds ? ` (${d.duration_seconds}s)` : ''}\n\n${d.result || ''}`
          updateLastMessage({ content: resultText, status: 'done' })

          window.dispatchEvent(new CustomEvent('runai:api-error', {
            detail: {
              message: d.status === 'failed' ? `Skill başarısız: ${skill.name}` : `Skill tamamlandı: ${skill.name}`,
              type: d.status === 'failed' ? 'error' : 'warning',
            },
          }))
        }
      } catch (e: any) {
        updateLastMessage({ content: `❌ Skill hatası: ${e.message}`, status: 'done' })
      } finally {
        setSending(false)
      }
      return true
    }

    // AgentBus commands — /code, /note, /event, /web, /find, /task, /analyze
    if (cmd.command === '/code' && cmd.args) {
      window.dispatchEvent(new CustomEvent('runai:agent-command', {
        detail: { target: 'runcode', action: 'explain', payload: { code: cmd.args } },
      }))
      addMessage({ role: 'assistant', content: '📝 Code editor açılıyor...', ts: ts(), agentName: 'System', status: 'done' })
      return true
    }

    if (cmd.command === '/note' && cmd.args) {
      window.dispatchEvent(new CustomEvent('runai:agent-command', {
        detail: { target: 'runpad', action: 'summarize', payload: { title: cmd.args } },
      }))
      addMessage({ role: 'assistant', content: '📄 Markdown editörü açılıyor...', ts: ts(), agentName: 'System', status: 'done' })
      return true
    }

    if (cmd.command === '/event' && cmd.args) {
      const parts = cmd.args.split(' ')
      const date = parts[0]
      const title = parts.slice(1).join(' ')
      window.dispatchEvent(new CustomEvent('runai:agent-command', {
        detail: { target: 'calendar', action: 'create-event', payload: { date, title } },
      }))
      addMessage({ role: 'assistant', content: '📅 Adding event to calendar...', ts: ts(), agentName: 'System', status: 'done' })
      return true
    }

    if (cmd.command === '/web' && cmd.args) {
      window.dispatchEvent(new CustomEvent('runai:agent-command', {
        detail: { target: 'runweb', action: 'navigate', payload: { url: cmd.args } },
      }))
      addMessage({ role: 'assistant', content: '🌐 Browser açılıyor...', ts: ts(), agentName: 'System', status: 'done' })
      return true
    }

    if (cmd.command === '/find' && cmd.args) {
      window.dispatchEvent(new CustomEvent('runai:agent-command', {
        detail: { target: 'runfiles', action: 'find-file', payload: { query: cmd.args } },
      }))
      addMessage({ role: 'assistant', content: '🔍 Dosya Yöneticisi açılıyor...', ts: ts(), agentName: 'System', status: 'done' })
      return true
    }

    if (cmd.command === '/task' && cmd.args) {
      window.dispatchEvent(new CustomEvent('runai:agent-command', {
        detail: { target: 'taskmanager', action: 'create-task', payload: { title: cmd.args } },
      }))
      addMessage({ role: 'assistant', content: '✅ Task yöneticisine görev ekleniyor...', ts: ts(), agentName: 'System', status: 'done' })
      return true
    }

    if (cmd.command === '/analyze') {
      addMessage({ role: 'assistant', content: '🔬 Aktif pencere analiz ediliyor...', ts: ts(), agentName: 'System', status: 'done' })
      window.dispatchEvent(new CustomEvent('runai:agent-command', {
        detail: { target: 'runcode', action: 'analyze', payload: {} },
      }))
      return true
    }

    // /next — Anne Filiz sıradaki task'ı önerir
    if (cmd.command === '/next') {
      addMessage({ role: 'assistant', content: '🎯 Sıradaki görev aranıyor...', ts: ts(), agentName: 'Anne Filiz', status: 'thinking' })
      setSending(true)
      try {
        const res = await apiFetch(`${API_URL}/api/tasks/next`)
        const d = await res.json() as { task?: { id: number; title: string; priority?: string; category?: string }; error?: string }
        if (d.task) {
          updateLastMessage({
            content: `🎯 **Sıradaki görev:** ${d.task.title}\n\nID: #${d.task.id}${d.task.priority ? ` | Öncelik: ${d.task.priority}` : ''}${d.task.category ? ` | Kategori: ${d.task.category}` : ''}\n\nBaşlamak için: \`/expand ${d.task.id}\``,
            status: 'done',
          })
        } else {
          updateLastMessage({ content: d.error ?? '✅ All tasks completed!', status: 'done' })
        }
      } catch { updateLastMessage({ content: '❌ Connection error', status: 'done' }) }
      finally { setSending(false) }
      return true
    }

    // /expand [id] — Baba Filiz task'ı subtask'lara böler
    if (cmd.command === '/expand' && cmd.args) {
      const taskId = parseInt(cmd.args)
      if (isNaN(taskId)) {
        addMessage({ role: 'assistant', content: '❌ Geçerli bir task ID girin: `/expand 123`', ts: ts(), agentName: 'System' })
        return true
      }
      addMessage({ role: 'assistant', content: `🔄 Task #${taskId} genişletiliyor...`, ts: ts(), agentName: 'Baba Filiz', status: 'thinking' })
      setSending(true)
      try {
        const res = await apiFetch(`${API_URL}/api/tasks/${taskId}/expand`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
        })
        const d = await res.json() as { ok?: boolean; subtask_count?: number; subtasks?: Array<{ title: string }>; error?: string }
        if (d.ok) {
          const subtaskList = d.subtasks?.map((s, i) => `  ${i + 1}. ${s.title}`).join('\n') ?? ''
          updateLastMessage({
            content: `✅ Task #${taskId} → ${d.subtask_count ?? 0} subtask oluşturuldu\n\n${subtaskList}`,
            status: 'done',
          })
        } else {
          updateLastMessage({ content: `❌ Could not expand: ${d.error ?? 'unknown'}`, status: 'done' })
        }
      } catch { updateLastMessage({ content: '❌ Connection error', status: 'done' }) }
      finally { setSending(false) }
      return true
    }

    // /social [text] — Social feed'e post at (KURAL 83: explicit user confirmation)
    if (cmd.command === '/social' && cmd.args) {
      const confirmed = window.confirm(
        `📢 Social'a paylaşılacak içerik:\n\n"${cmd.args.slice(0, 200)}${cmd.args.length > 200 ? '…' : ''}"\n\nBu içerik herkese görünür olacak. Onaylıyor musun?`
      )
      if (!confirmed) {
        addMessage({ role: 'assistant', content: '❌ Post atılmadı (iptal edildi).', ts: ts(), agentName: 'Social', status: 'done' })
        return true
      }
      addMessage({ role: 'assistant', content: '📝 Post atılıyor...', ts: ts(), agentName: 'Social', status: 'thinking' })
      setSending(true)
      try {
        const res = await apiFetch(`${API_URL}/api/social/posts`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: cmd.args, post_type: 'user_post' }),
        })
        const d = await res.json() as { ok?: boolean; id?: number; error?: string }
        if (d.ok || d.id) {
          updateLastMessage({ content: `✅ Post atıldı! (ID: ${d.id})`, status: 'done' })
          window.dispatchEvent(new CustomEvent('runai:social-mention', {
            detail: { author: 'You', content: cmd.args },
          }))
        } else {
          updateLastMessage({ content: `❌ Could not post: ${d.error ?? 'unknown'}`, status: 'done' })
        }
      } catch { updateLastMessage({ content: '❌ Connection error', status: 'done' }) }
      finally { setSending(false) }
      return true
    }

    // /brain [title] [content] — Brain Wiki'ye bilgi ekle
    if (cmd.command === '/brain' && cmd.args) {
      const parts = cmd.args.split(' ')
      const title = parts[0]
      const content = parts.slice(1).join(' ')
      if (!title || !content) {
        addMessage({ role: 'assistant', content: '❌ Kullanım: `/brain Başlık İçerik buraya yazılır`', ts: ts(), agentName: 'System' })
        return true
      }
      addMessage({ role: 'assistant', content: '🧠 Brain Wiki\'ye ekleniyor...', ts: ts(), agentName: 'Brain', status: 'thinking' })
      setSending(true)
      try {
        const res = await apiFetch(`${API_URL}/api/brain/wiki`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title, content, category: 'manual', source: 'chat' }),
        })
        const d = await res.json() as { ok?: boolean; id?: number; error?: string }
        if (d.ok) {
          updateLastMessage({ content: `✅ Brain Wiki'ye eklendi: "${title}" (ID: ${d.id})`, status: 'done' })
        } else {
          updateLastMessage({ content: `❌ Could not add: ${d.error ?? 'unknown'}`, status: 'done' })
        }
      } catch { updateLastMessage({ content: '❌ Connection error', status: 'done' }) }
      finally { setSending(false) }
      return true
    }

    // /wiki [query] — Brain Wiki'de ara
    if (cmd.command === '/wiki' && cmd.args) {
      addMessage({ role: 'assistant', content: `🔍 Wiki'de aranıyor: "${cmd.args}"...`, ts: ts(), agentName: 'Brain', status: 'thinking' })
      setSending(true)
      try {
        const res = await apiFetch(`${API_URL}/api/brain/wiki/search?q=${encodeURIComponent(cmd.args)}&limit=5`)
        const d = await res.json() as { results?: Array<{ title: string; content?: string }>; error?: string }
        if (d.results && d.results.length > 0) {
          const resultText = d.results.map((r, i) => `${i + 1}. **${r.title}**\n   ${(r.content ?? '').slice(0, 100)}...`).join('\n\n')
          updateLastMessage({ content: `🧠 Wiki sonuçları (${d.results.length}):\n\n${resultText}`, status: 'done' })
        } else {
          updateLastMessage({ content: `🧠 "${cmd.args}" no results found for.`, status: 'done' })
        }
      } catch { updateLastMessage({ content: '❌ Connection error', status: 'done' }) }
      finally { setSending(false) }
      return true
    }

    // Unhandled slash command — let it fall through to normal send
    return false
  }, [addMessage, updateLastMessage, setSending, setCategory, speak, activeAgentName])

  return { handleSlashCommand }
}
